import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Briefcase, Plus, MagnifyingGlass, Trash, Pencil, Sparkle, ArrowsClockwise,
  ChartPieSlice, Warning, CheckCircle, Target as TargetIcon, X,
  TrendUp, Lightning, Export,
} from "@phosphor-icons/react";


// ----------- Formatters (kept tiny + local; portfolio is the only consumer) -----------
const fmt$ = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1000) return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtPct = (n, signed = false) => {
  if (n === null || n === undefined) return "—";
  const v = Number(n);
  return `${signed && v >= 0 ? "+" : ""}${v.toFixed(2)}%`;
};


// ----------- Add holding modal -----------
function AddHoldingModal({ onClose, onAdded, editing }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(editing
    ? { id: editing.asset_id, type: editing.asset_type, symbol: editing.symbol, name: editing.name }
    : null);
  const [quantity, setQuantity] = useState(editing?.quantity?.toString() ?? "");
  const [costBasis, setCostBasis] = useState(editing?.cost_basis?.toString() ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const search = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const r = await axios.get(`${API}/search?q=${encodeURIComponent(query.trim())}`, { withCredentials: true });
      setResults((r.data?.results || r.data || []).slice(0, 10));
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const submit = async () => {
    setError("");
    if (!selected || !quantity || !costBasis) {
      setError("Please complete all fields");
      return;
    }
    const qty = parseFloat(quantity);
    const cost = parseFloat(costBasis);
    if (!(qty > 0)) return setError("Quantity must be > 0");
    if (!(cost >= 0)) return setError("Cost basis must be ≥ 0");
    setSubmitting(true);
    try {
      if (editing) {
        await axios.patch(`${API}/portfolio/holdings/${editing.id}`,
          { quantity: qty, cost_basis: cost },
          { withCredentials: true });
      } else {
        await axios.post(`${API}/portfolio/holdings`, {
          asset_id: selected.id,
          asset_type: selected.type,
          symbol: selected.symbol,
          name: selected.name || selected.symbol,
          quantity: qty,
          cost_basis: cost,
        }, { withCredentials: true });
      }
      onAdded();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save holding");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-zinc-950 border border-amber-500/30 rounded-sm max-w-lg w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="add-holding-modal">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-heading text-lg font-bold text-zinc-100">{editing ? "Edit Holding" : "Add Holding"}</h3>
            <p className="text-[11px] text-zinc-500">{editing ? `${editing.symbol} — update quantity or cost basis` : "Search any asset on the platform"}</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300" data-testid="modal-close"><X size={18} /></button>
        </div>

        {!editing && (
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1.5">Search asset</label>
            <div className="flex items-center gap-2">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") search(); }}
                placeholder="bitcoin, NVDA, SPY..."
                data-testid="holding-search-input"
                className="flex-1 bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50"
              />
              <Button onClick={search} disabled={searching} size="sm" data-testid="holding-search-btn" className="bg-amber-500/20 border border-amber-500/50 text-amber-200 hover:bg-amber-500/30 h-9">
                {searching ? <ArrowsClockwise size={12} className="animate-spin" /> : <MagnifyingGlass size={12} />}
              </Button>
            </div>
            {results.length > 0 && (
              <div className="mt-2 max-h-48 overflow-y-auto border border-zinc-800 rounded-sm divide-y divide-zinc-800/60">
                {results.map((r, i) => (
                  <button
                    key={`${r.type}-${r.id}-${i}`}
                    onClick={() => { setSelected(r); setResults([]); setQuery(""); }}
                    data-testid={`holding-result-${i}`}
                    className="w-full text-left px-3 py-2 hover:bg-zinc-900 flex items-center justify-between gap-2"
                  >
                    <div>
                      <div className="text-sm text-zinc-100 font-medium">{r.symbol?.toUpperCase()} <span className="text-zinc-500 text-xs ml-1">{r.name}</span></div>
                      <div className="text-[10px] text-zinc-600">{r.type}{r.chain ? ` · ${r.chain}` : ""}</div>
                    </div>
                    <Badge className="text-[8px] px-1.5 py-0 h-4 bg-zinc-800 border-0 text-zinc-400 capitalize">{r.type}</Badge>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {selected && (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-3 space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle size={14} weight="fill" className="text-emerald-400" />
              <span className="font-semibold text-zinc-100">{selected.symbol?.toUpperCase()}</span>
              <span className="text-zinc-500 text-xs">{selected.name}</span>
              <Badge className="text-[8px] px-1.5 py-0 h-4 bg-zinc-800 border-0 text-zinc-400 capitalize">{selected.type}</Badge>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1.5">Quantity</label>
            <input
              type="number"
              step="any"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="0.5"
              data-testid="holding-quantity"
              className="w-full bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-amber-500/50"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1.5">Total cost (USD)</label>
            <input
              type="number"
              step="any"
              value={costBasis}
              onChange={(e) => setCostBasis(e.target.value)}
              placeholder="50000"
              data-testid="holding-cost"
              className="w-full bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-amber-500/50"
            />
            {quantity > 0 && costBasis > 0 && (
              <p className="text-[9px] text-zinc-600 mt-0.5">avg = ${(parseFloat(costBasis) / parseFloat(quantity)).toFixed(4)} each</p>
            )}
          </div>
        </div>

        {error && <p className="text-[11px] text-red-400" data-testid="holding-error">{error}</p>}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-800/60">
          <button onClick={onClose} className="text-[11px] px-3 py-2 rounded-sm bg-zinc-800/60 border border-zinc-700 text-zinc-300 hover:bg-zinc-800">Cancel</button>
          <Button onClick={submit} disabled={submitting || !selected || !quantity || !costBasis} size="sm" data-testid="holding-submit" className="bg-amber-500/30 border border-amber-500/60 text-amber-100 hover:bg-amber-500/40">
            {submitting ? <ArrowsClockwise size={12} className="animate-spin mr-1" /> : <Plus size={12} weight="bold" className="mr-1" />}
            {editing ? "Save changes" : "Add to portfolio"}
          </Button>
        </div>
      </div>
    </div>
  );
}


// ----------- Pure-CSS donut chart for allocation (no Recharts dependency for speed) -----------
function AllocationDonut({ holdings }) {
  // Order matters — use a fixed colour rotation so the legend matches
  const palette = ["#f59e0b", "#10b981", "#3b82f6", "#a855f7", "#ec4899", "#06b6d4", "#84cc16", "#f97316"];
  const total = holdings.reduce((s, h) => s + (h.pct || 0), 0);
  if (total <= 0) return <p className="text-[11px] text-zinc-600 italic">No allocation yet.</p>;

  let acc = 0;
  const stops = holdings.map((h, i) => {
    const start = acc;
    acc += h.pct;
    return { ...h, color: palette[i % palette.length], start, end: acc };
  });
  const conicGradient = stops
    .map(s => `${s.color} ${s.start}% ${s.end}%`)
    .join(", ");

  return (
    <div className="flex items-center gap-5">
      <div
        className="w-32 h-32 rounded-full shrink-0 relative"
        style={{ background: `conic-gradient(${conicGradient})` }}
        data-testid="allocation-donut"
      >
        <div className="absolute inset-3 bg-zinc-950 rounded-full flex items-center justify-center">
          <ChartPieSlice size={24} className="text-zinc-700" />
        </div>
      </div>
      <ul className="flex-1 space-y-1 min-w-0">
        {stops.map(s => (
          <li key={s.id} className="flex items-center gap-2 text-[11px]">
            <span className="w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: s.color }} />
            <span className="text-zinc-300 truncate flex-1">{s.symbol}</span>
            <span className="font-data text-zinc-100">{s.pct.toFixed(1)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}


// ----------- Risk metric tile -----------
function RiskTile({ label, value, sub, accent = "zinc", testid }) {
  const accents = {
    emerald: "border-emerald-500/30 text-emerald-300",
    amber: "border-amber-500/30 text-amber-300",
    red: "border-red-500/30 text-red-300",
    zinc: "border-zinc-800 text-zinc-200",
  };
  return (
    <div className={`bg-zinc-950/50 border ${accents[accent]} rounded-sm p-3`} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <div className="text-lg font-data font-semibold leading-tight">{value}</div>
      {sub && <div className="text-[10px] text-zinc-600 mt-1">{sub}</div>}
    </div>
  );
}


// ----------- AI Insights box -----------
function AIInsights({ insights, generatedAt, onRefresh, refreshing, hasHoldings }) {
  if (!insights) {
    return (
      <div className="bg-zinc-900/50 border border-amber-500/30 rounded-sm p-4" data-testid="ai-insights-empty">
        <div className="flex items-center gap-2 mb-1">
          <Sparkle size={14} className="text-amber-400" weight="fill" />
          <span className="text-sm font-semibold text-zinc-200">AI Insights</span>
        </div>
        <p className="text-[11px] text-zinc-500 mb-3">
          Get a Grok-powered review of your portfolio's strengths, risks, and one tactical move. Cached for 24h.
        </p>
        <Button onClick={onRefresh} disabled={!hasHoldings || refreshing} size="sm" data-testid="ai-insights-generate"
          className="bg-amber-500/20 border border-amber-500/50 text-amber-200 hover:bg-amber-500/30">
          {refreshing ? <ArrowsClockwise size={12} className="animate-spin mr-1" /> : <Sparkle size={12} className="mr-1" weight="fill" />}
          Generate insights
        </Button>
      </div>
    );
  }

  const ageHours = generatedAt ? Math.floor((Date.now() - new Date(generatedAt).getTime()) / 3_600_000) : null;
  return (
    <div className="bg-zinc-900/50 border border-amber-500/30 rounded-sm p-4 space-y-3" data-testid="ai-insights-panel">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkle size={14} className="text-amber-400" weight="fill" />
            <span className="text-sm font-semibold text-zinc-200">AI Insights</span>
            {ageHours !== null && (
              <Badge className="text-[8px] px-1.5 py-0 h-4 bg-zinc-800 border-0 text-zinc-500">
                {ageHours}h ago
              </Badge>
            )}
          </div>
          <p className="text-[12px] text-amber-200 leading-relaxed font-medium">{insights.headline}</p>
        </div>
        <button
          onClick={onRefresh}
          disabled={refreshing}
          title="Force refresh (uses Grok credits)"
          className="text-zinc-500 hover:text-zinc-300 p-1 rounded-sm border border-zinc-800 disabled:opacity-40"
          data-testid="ai-insights-refresh"
        >
          <ArrowsClockwise size={11} className={refreshing ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-emerald-500 mb-1">Strengths</div>
          <ul className="space-y-0.5">
            {(insights.strengths || []).map((s, i) => (
              <li key={i} className="text-[11px] text-zinc-300">· {s}</li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-red-500 mb-1">Risks</div>
          <ul className="space-y-0.5">
            {(insights.risks || []).map((s, i) => (
              <li key={i} className="text-[11px] text-zinc-300">· {s}</li>
            ))}
          </ul>
        </div>
      </div>

      {insights.moves?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-amber-500 mb-1">Tactical moves</div>
          <ul className="space-y-1">
            {insights.moves.map((m, i) => (
              <li key={i} className="text-[11px] text-zinc-300">
                <span className="text-amber-300 font-semibold capitalize">{m.action}</span> {m.what} —{" "}
                <span className="text-zinc-400">{m.why}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {insights.nugget && (
        <div className="bg-zinc-950/60 border border-zinc-800 rounded-sm px-3 py-2">
          <div className="text-[9px] uppercase tracking-wider text-zinc-500 flex items-center gap-1 mb-1">
            <Lightning size={9} /> Goose nugget
          </div>
          <p className="text-[11px] text-zinc-300 italic leading-relaxed">{insights.nugget}</p>
        </div>
      )}
    </div>
  );
}


// ----------- What-if rebalance simulator -----------
function RebalanceSimulator({ holdings, totalValue, onClose }) {
  const [targets, setTargets] = useState(() => Object.fromEntries(holdings.map(h => [h.id, h.allocation_pct])));
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  const targetSum = Object.values(targets).reduce((s, v) => s + (parseFloat(v) || 0), 0);

  const runSim = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/portfolio/simulate`,
        { targets: Object.fromEntries(Object.entries(targets).map(([k, v]) => [k, parseFloat(v) || 0])) },
        { withCredentials: true });
      setResult(r.data);
    } catch (err) {
      setResult({ error: err.response?.data?.detail || "Simulation failed" });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-zinc-950 border border-amber-500/30 rounded-sm max-w-2xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="rebalance-modal">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-heading text-lg font-bold text-zinc-100">What-if rebalance</h3>
            <p className="text-[11px] text-zinc-500">Set a target % for each holding. We'll show the buy/sell deltas needed to get there. Total: ${totalValue.toLocaleString()}</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X size={18} /></button>
        </div>

        <div className="space-y-2">
          {holdings.map(h => (
            <div key={h.id} className="grid grid-cols-[100px_1fr_60px_60px] gap-2 items-center">
              <span className="text-sm font-data font-semibold text-zinc-100">{h.symbol}</span>
              <input
                type="range"
                min="0"
                max="100"
                step="0.5"
                value={targets[h.id] || 0}
                onChange={(e) => setTargets({ ...targets, [h.id]: e.target.value })}
                data-testid={`sim-slider-${h.symbol}`}
                className="accent-amber-500"
              />
              <span className="text-[11px] font-data text-amber-300 text-right">{Number(targets[h.id] || 0).toFixed(1)}%</span>
              <span className="text-[10px] text-zinc-600 text-right">was {h.allocation_pct?.toFixed(1)}%</span>
            </div>
          ))}
        </div>

        <div className={`text-[11px] flex items-center gap-2 ${Math.abs(targetSum - 100) <= 0.5 ? "text-emerald-400" : "text-amber-400"}`}>
          {Math.abs(targetSum - 100) <= 0.5 ? <CheckCircle size={11} weight="fill" /> : <Warning size={11} weight="fill" />}
          Total: {targetSum.toFixed(1)}% {Math.abs(targetSum - 100) <= 0.5 ? "" : "(should sum to 100%)"}
        </div>

        <Button onClick={runSim} disabled={running} size="sm" data-testid="sim-run" className="bg-amber-500/30 border border-amber-500/60 text-amber-100 hover:bg-amber-500/40 w-full">
          {running ? <ArrowsClockwise size={12} className="animate-spin mr-1" /> : <TargetIcon size={12} weight="bold" className="mr-1" />}
          Calculate trades
        </Button>

        {result?.error && <p className="text-xs text-red-400">{result.error}</p>}
        {result?.deltas && (
          <div className="border-t border-zinc-800 pt-3 space-y-1.5" data-testid="sim-results">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Trades to execute</div>
            {result.deltas.map(d => (
              <div key={d.holding_id} className="flex items-center justify-between text-[11px] py-1 border-b border-zinc-900/60 last:border-0">
                <span className="font-data font-semibold text-zinc-100">{d.symbol}</span>
                <span className={`font-data ${
                  d.action === "buy" ? "text-emerald-400" : d.action === "sell" ? "text-red-400" : "text-zinc-500"
                }`}>
                  {d.action.toUpperCase()} {Math.abs(d.delta_qty).toFixed(4)} ({fmt$(Math.abs(d.delta_value))})
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// ----------- Main panel -----------
export default function PortfolioPanel() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showSim, setShowSim] = useState(false);
  const [insights, setInsights] = useState(null);
  const [insightsRefreshing, setInsightsRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [pf, ai] = await Promise.all([
        axios.get(`${API}/portfolio`, { withCredentials: true }),
        axios.get(`${API}/portfolio/insights`, { withCredentials: true }).catch(() => ({ data: { insights: null } })),
      ]);
      setData(pf.data);
      setInsights(ai.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load portfolio");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refreshInsights = async () => {
    setInsightsRefreshing(true);
    try {
      const r = await axios.post(`${API}/portfolio/insights/refresh`, {}, { withCredentials: true });
      setInsights(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || "AI insights failed");
    } finally {
      setInsightsRefreshing(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this holding from your portfolio?")) return;
    try {
      await axios.delete(`${API}/portfolio/holdings/${id}`, { withCredentials: true });
      load();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to remove");
    }
  };

  const exportToGoal = async () => {
    const name = window.prompt("Goal name?", "Hit $100k portfolio");
    if (!name) return;
    const targetStr = window.prompt(`Target amount in USD? (current portfolio: ${fmt$(data.totals.total_value)})`, "100000");
    if (!targetStr) return;
    const target = parseFloat(targetStr);
    if (!(target > data.totals.total_value)) {
      window.alert("Target must exceed current portfolio value");
      return;
    }
    const targetDate = window.prompt("Target date (YYYY-MM-DD)?", "2030-01-01");
    if (!targetDate) return;
    try {
      await axios.post(`${API}/portfolio/export-to-goal`,
        { name, category: "wealth", target_amount: target, target_date: targetDate, monthly_contribution: 500, risk: "balanced" },
        { withCredentials: true });
      navigate("/golden-goals");
    } catch (err) {
      setError(err.response?.data?.detail || "Export failed");
    }
  };

  const enriched = data?.holdings || [];
  const totals = data?.totals || {};
  const alloc = data?.allocations || {};
  const risk = data?.risk || {};

  const sharpeAccent = useMemo(() => {
    const s = risk.sharpe_ratio;
    if (s == null) return "zinc";
    if (s >= 1) return "emerald";
    if (s >= 0) return "amber";
    return "red";
  }, [risk]);

  const divAccent = useMemo(() => {
    const d = alloc.diversification_score;
    if (d == null) return "zinc";
    if (d >= 60) return "emerald";
    if (d >= 30) return "amber";
    return "red";
  }, [alloc]);

  if (loading) {
    return <div className="text-zinc-500 text-sm py-12 text-center"><ArrowsClockwise size={14} className="animate-spin inline mr-1" /> Loading portfolio…</div>;
  }
  if (error && !data) {
    return <div className="text-red-400 text-sm py-12 text-center" data-testid="portfolio-error">{error}</div>;
  }

  return (
    <div className="space-y-5" data-testid="portfolio-panel">
      {/* HEADER */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-base font-bold text-zinc-100 flex items-center gap-2">
            <Briefcase size={14} weight="bold" className="text-amber-400" /> Portfolio Tracker
          </h2>
          <p className="text-[10px] text-zinc-500">Live prices, allocation, risk metrics & AI insights. Gold-tier only.</p>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={load} className="text-[10px] text-zinc-400 hover:text-zinc-200 px-2 py-1 border border-zinc-800 rounded-sm" data-testid="portfolio-refresh">
            <ArrowsClockwise size={11} className="inline" />
          </button>
          {enriched.length > 0 && (
            <>
              <Button onClick={() => setShowSim(true)} size="sm" data-testid="rebalance-btn"
                className="bg-zinc-900 border border-zinc-800 text-zinc-300 hover:bg-zinc-800 h-7 text-[10px]">
                <TargetIcon size={11} weight="bold" className="mr-1" /> What-if
              </Button>
              <Button onClick={exportToGoal} size="sm" data-testid="export-goal-btn"
                className="bg-zinc-900 border border-zinc-800 text-zinc-300 hover:bg-zinc-800 h-7 text-[10px]">
                <Export size={11} weight="bold" className="mr-1" /> To Goal
              </Button>
            </>
          )}
          <Button onClick={() => { setEditing(null); setShowAdd(true); }} size="sm" data-testid="add-holding-btn"
            className="bg-amber-500/20 border border-amber-500/50 text-amber-200 hover:bg-amber-500/30 h-7 text-[10px]">
            <Plus size={11} weight="bold" className="mr-1" /> Add Holding
          </Button>
        </div>
      </div>

      {/* EMPTY STATE */}
      {enriched.length === 0 && (
        <div className="bg-zinc-900/40 border border-dashed border-zinc-800 rounded-sm p-12 text-center" data-testid="portfolio-empty">
          <Briefcase size={40} weight="thin" className="text-zinc-700 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-zinc-300 mb-1">Build your portfolio</h3>
          <p className="text-[12px] text-zinc-500 mb-4 max-w-md mx-auto">Add the assets you actually hold — we'll pull live prices, calculate your real-time P&amp;L, and run the same risk analysis Silly Goose uses on his own bag.</p>
          <Button onClick={() => { setEditing(null); setShowAdd(true); }} size="sm" data-testid="empty-add-btn"
            className="bg-amber-500/20 border border-amber-500/50 text-amber-200 hover:bg-amber-500/30">
            <Plus size={12} weight="bold" className="mr-1" /> Add your first holding
          </Button>
        </div>
      )}

      {/* TOTALS + ALLOCATION + RISK */}
      {enriched.length > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <RiskTile label="Total Value" value={fmt$(totals.total_value)} sub={`${totals.holdings_count} holdings`} accent="amber" testid="kpi-total-value" />
            <RiskTile label="Cost Basis" value={fmt$(totals.total_cost)} sub="What you paid" />
            <RiskTile label="P&L" value={fmt$(totals.total_pnl_abs)} sub={fmtPct(totals.total_pnl_pct, true)} accent={totals.total_pnl_pct >= 0 ? "emerald" : "red"} testid="kpi-pnl" />
            <RiskTile label="Diversification" value={`${alloc.diversification_score}/100`} sub={`Largest: ${alloc.largest_pct?.toFixed(1)}%`} accent={divAccent} testid="kpi-diversification" />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            {/* Allocation donut */}
            <div className="bg-zinc-900/40 border border-zinc-800 rounded-sm p-4">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3 flex items-center gap-1.5">
                <ChartPieSlice size={11} /> Allocation
              </div>
              <AllocationDonut holdings={alloc.by_holding || []} />
              <div className="mt-3 pt-3 border-t border-zinc-800/60 flex items-center gap-3 flex-wrap text-[10px] text-zinc-500">
                {Object.entries(alloc.by_type || {}).map(([t, p]) => (
                  <span key={t} className="capitalize">{t}: <span className="font-data text-zinc-300">{p}%</span></span>
                ))}
              </div>
            </div>

            {/* Risk metrics */}
            <div className="bg-zinc-900/40 border border-zinc-800 rounded-sm p-4">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3 flex items-center gap-1.5">
                <TrendUp size={11} /> 90-day risk profile
              </div>
              <div className="grid grid-cols-3 gap-3">
                <RiskTile
                  label="Volatility"
                  value={risk.volatility_annual_pct != null ? `${risk.volatility_annual_pct}%` : "—"}
                  sub="annualized"
                  accent={risk.volatility_annual_pct != null ? (risk.volatility_annual_pct < 30 ? "emerald" : risk.volatility_annual_pct < 60 ? "amber" : "red") : "zinc"}
                  testid="risk-vol"
                />
                <RiskTile
                  label="Sharpe"
                  value={risk.sharpe_ratio != null ? risk.sharpe_ratio.toFixed(2) : "—"}
                  sub="risk-adj return"
                  accent={sharpeAccent}
                  testid="risk-sharpe"
                />
                <RiskTile
                  label="Max DD"
                  value={risk.max_drawdown_pct != null ? `${risk.max_drawdown_pct}%` : "—"}
                  sub="peak-to-trough"
                  accent={risk.max_drawdown_pct != null ? (risk.max_drawdown_pct > -10 ? "emerald" : risk.max_drawdown_pct > -30 ? "amber" : "red") : "zinc"}
                  testid="risk-dd"
                />
              </div>
              {risk.data_points < 30 && (
                <p className="text-[10px] text-zinc-600 italic mt-3">⚠ Limited price history available — metrics improve as data fills in.</p>
              )}
            </div>
          </div>

          {/* AI Insights */}
          <AIInsights
            insights={insights?.insights}
            generatedAt={insights?.generated_at}
            onRefresh={refreshInsights}
            refreshing={insightsRefreshing}
            hasHoldings={enriched.length > 0}
          />

          {/* HOLDINGS TABLE */}
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-sm overflow-hidden" data-testid="holdings-table">
            <div className="grid grid-cols-[1.5fr_repeat(5,1fr)_60px] gap-2 px-3 py-2 border-b border-zinc-800/80 text-[9px] uppercase tracking-wider text-zinc-500">
              <span>Asset</span><span className="text-right">Qty</span><span className="text-right">Avg Buy</span>
              <span className="text-right">Price</span><span className="text-right">Value</span><span className="text-right">P&amp;L</span><span></span>
            </div>
            {enriched.map((h) => (
              <div key={h.id} className="grid grid-cols-[1.5fr_repeat(5,1fr)_60px] gap-2 px-3 py-2.5 border-b border-zinc-900/40 last:border-0 items-center text-[12px] hover:bg-zinc-900/40" data-testid={`holding-${h.symbol}`}>
                <div>
                  <div className="font-semibold text-zinc-100 flex items-center gap-1.5">
                    {h.symbol}
                    <span className="text-[8px] px-1 py-0 bg-zinc-800 text-zinc-400 rounded-sm capitalize">{h.asset_type}</span>
                  </div>
                  <div className="text-[9px] text-zinc-600 truncate">{h.name}</div>
                </div>
                <span className="text-right font-data text-zinc-300">{h.quantity}</span>
                <span className="text-right font-data text-zinc-500">${h.avg_buy_price?.toFixed(2)}</span>
                <span className="text-right font-data text-zinc-200">${h.current_price?.toFixed(h.current_price < 1 ? 4 : 2)}</span>
                <span className="text-right font-data text-zinc-100">{fmt$(h.current_value)}</span>
                <span className={`text-right font-data ${h.pnl_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {fmtPct(h.pnl_pct, true)}
                </span>
                <div className="flex items-center justify-end gap-1">
                  <button onClick={() => { setEditing(h); setShowAdd(true); }} className="text-zinc-500 hover:text-amber-400 p-1" data-testid={`edit-${h.symbol}`}>
                    <Pencil size={10} />
                  </button>
                  <button onClick={() => remove(h.id)} className="text-zinc-500 hover:text-red-400 p-1" data-testid={`delete-${h.symbol}`}>
                    <Trash size={10} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {showAdd && <AddHoldingModal onClose={() => { setShowAdd(false); setEditing(null); }} onAdded={load} editing={editing} />}
      {showSim && <RebalanceSimulator holdings={enriched} totalValue={totals.total_value} onClose={() => setShowSim(false)} />}
    </div>
  );
}
